import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Autocomplete, Box, CircularProgress, Collapse, debounce, FormControl, FormHelperText, Grid, IconButton, InputLabel, ListItem, ListItemText, MenuItem, Select, Snackbar, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { ConfirmDialog, MdiIcon, Table } from '@components';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { mdiChevronUp, mdiContentSave, mdiMagnify, mdiPencil, mdiPlus, mdiTrashCan } from '@mdi/js';
import { useModal } from 'mui-modal-provider';
import moment from 'moment';
import { InfoDialog } from './infoDialog';
const Oportunidades = ({ $state, CrmService, user, client }) => {
    const [userId, setUserId] = useState();
    const [openSnackBar, setOpenSnackBar] = useState(false);
    const [snackBarMessage, setSnackBarMessage] = useState('');
    const [snackBarSeverity, setSnackBarSeverity] = useState(undefined);
    const [data, setData] = useState();
    const [loading, setLoading] = useState(true);
    const [refetching, setRefetching] = useState(false);
    const [error, setError] = useState(false);
    const [filter, setFilter] = useState([]); // filtros a aplicar na tabela
    const [sort, setSort] = useState([]); // ordenação da tabela
    const [options, setOptions] = useState({
        filter: [], // exemplo -> [{name: 'nome', type: 'TEXT'}, {name: 'ultimoContacto', type: 'DATE', options: {format: 'YYYY-MM-DD'}}, ...]
        sort: [] // exemplo -> ['id', 'nome', 'ultimoContacto', 'contactoTelefonico', 'nif', 'email', 'morada', 'localidade']
    });
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 5
    });
    const [oportunidade, setOportunidade] = useState(null);
    const [departamentos, setDepartamentos] = useState([]);
    const [dep, setDep] = useState(''); // Estado do Select como string
    // AutoComplete
    const [loadAutoComplete, setLoadAutoComplete] = useState(false);
    const [optionsAutoComplete, setOptionsAutoComplete] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [selected, setSelected] = useState(null);
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const noFetchRef = useRef([]); // contactos já associados, não pesquisar os mesmos
    const limit = 5; // Número de itens por página
    const [expand, setExpand] = useState(false);
    const { showModal } = useModal();
    const validationSchema = yup.object().shape({
        servico: yup.string().required('Preenchimento obrigatório'),
        coreDepartamentoId: yup
            .number()
            .typeError('Selecione uma opção válida') // Caso o valor não seja numérico
            .required('Preenchimento obrigatório')
            .notOneOf([0], 'Selecione uma opção válida'), // Garante que não seja 0
        colaboradorOrigem: yup
            .number()
            .typeError('Selecione uma opção válida') // Caso o valor não seja numérico
            .required('Preenchimento obrigatório')
            .notOneOf([0], 'Selecione uma opção válida'), // Garante que não seja 0
        observacoes: yup.string().max(255, 'Observações não podem ter mais de 255 caracteres')
    });
    const formik = useFormik({
        initialValues: oportunidade ?? {
            id: 0,
            coreClienteId: client,
            funcionarioId: userId,
            colaboradorOrigem: 0,
            coreDepartamentoId: 0,
            servico: '',
            observacoes: '',
            active: 1
        },
        validationSchema: validationSchema,
        validateOnMount: false,
        validateOnChange: true,
        enableReinitialize: true,
        onSubmit: async (values) => {
            let currentDate = moment().format();
            if (!values.dataRegisto) {
                values.dataRegisto = currentDate;
            }
            else {
                values.dataEdit = currentDate;
            }
            console.log(values);
            try {
                await CrmService.updateOportunidade(values);
            }
            catch (error) {
                console.log('Erro ', error);
            }
            setRefetching(true);
            fetchData();
            setExpand(false);
        }
    });
    // Filtragem dentro de cada coluna
    // Modo de filtro (igual a, contém, começa com... )
    const getType = (type) => {
        switch (type) {
            case 'contains':
                return '$ilike';
            case 'startsWith':
                return '$sw';
            case 'notEquals':
                return '$not';
            case 'empty':
                return '$null';
            case 'notEmpty':
                return '$notNull';
            case 'greaterThan':
                return '$gt';
            case 'greaterThanOrEqualTo':
                return '$gte';
            case 'lessThan':
                return '$lt';
            case 'lessThanOrEqualTo':
                return '$lte';
            default:
                return '$eq';
        }
    };
    const clientColumns = useMemo(() => [
        {
            accessorKey: 'servico',
            header: 'Serviço',
            size: 150
        },
        {
            accessorKey: 'Departamento.id',
            exportKey: 'Departamento.designacao',
            header: 'Área',
            size: 120,
            filterSelectOptions: departamentos.map(r => {
                return {
                    label: r.designacao,
                    value: r.id
                };
            }),
            Cell: ({ row }) => row.original?.Departamento?.designacao
        },
        {
            accessorKey: 'observacoes',
            header: 'Observações',
            size: 200,
            Cell: ({ cell }) => (<div style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: '250px' // Defina conforme necessário
                }} title={cell.getValue()} // Mostra o texto completo ao passar o rato
            >
            {cell.getValue()}
          </div>)
        },
        {
            accessorKey: 'ColaboradorOrigem.name',
            header: 'Colaborador Origem',
            size: 150
        }
    ], [departamentos]);
    const vendedorColumns = useMemo(() => [
        {
            accessorKey: 'Cliente.nome',
            header: 'Cliente',
            size: 150
        },
        {
            accessorKey: 'dataRegisto',
            header: 'Data Registo',
            size: 200,
            Cell: ({ row }) => (moment(row.original.dataRegisto).isValid() ? moment(row.original.dataRegisto).format('DD/MM/YYYY') : 'N/D')
        },
        {
            accessorKey: 'servico',
            header: 'Serviço',
            size: 140
        },
        {
            accessorKey: 'Departamento.id',
            exportKey: 'Departamento.designacao',
            header: 'Área',
            size: 120,
            filterSelectOptions: departamentos.map(r => {
                return {
                    label: r.designacao,
                    value: r.id
                };
            }),
            Cell: ({ row }) => row.original?.Departamento?.designacao
        },
        {
            accessorKey: 'ColaboradorOrigem.name',
            header: 'Colaborador Origem',
            size: 150
        }
    ], [departamentos]);
    const initialFetch = async () => {
        if (loading) {
            const id = await CrmService.getUserId();
            setUserId(id);
            const dep = await CrmService.getDepartamentos();
            setDepartamentos(dep);
            // opt -> vai buscar os campos filtraveis
            const opt = await CrmService.optionsOportunidades();
            setOptions(opt);
            setLoading(false);
        }
    };
    const fetchData = async () => {
        setError(false);
        try {
            let filters = {};
            filter.forEach(r => {
                filters[r.id] = `${getType(r.type)}:${r.value}`;
            });
            let sorter = [];
            sort.forEach(r => {
                sorter.push(`${r.id}:${r.desc ? 'DESC' : 'ASC'}`);
            });
            if (client !== undefined) {
                filters['coreClienteId'] = `$eq:${client}`;
            }
            const aux = await CrmService.findAllOportunidades({
                limit: pagination.pageSize,
                page: pagination.pageIndex + 1,
                filter: filters,
                sortBy: sorter
            });
            // Update URL
            $state.go($state.current.name, {
                limit: pagination.pageSize,
                page: pagination.pageIndex + 1,
                sortBy: sorter,
                filter: Object.keys(filter).length > 0 ? JSON.stringify(filters) : undefined
            }, {
                notify: false,
                reload: false,
                location: 'replace',
                inherit: true
            });
            setData(aux);
        }
        catch (e) {
            console.log(e);
            setError(true);
        }
        setRefetching(false);
    };
    const handleSelectChange = (event) => {
        const selectedId = event.target.value;
        console.log(selectedId);
        setDep(selectedId);
        formik.setFieldValue('coreDepartamentoId', Number(selectedId)); // Converte para número no Formik
    };
    const fetchOptions = useCallback(debounce(async (query, currentPage) => {
        if (!query || !hasMore)
            return;
        setLoadAutoComplete(true);
        try {
            const response = await CrmService.funcionarioAutocomplete(query, currentPage, limit, noFetchRef.current);
            if (response.length < limit) {
                setHasMore(false); // Não há mais dados
            }
            setOptionsAutoComplete(prev => [...prev, ...response]); // Concatena os dados
        }
        catch (error) {
            console.error('Erro ao carregar opções:', error);
        }
        finally {
            setLoadAutoComplete(false);
        }
    }, 500), [hasMore] // Atualiza se `hasMore` mudar
    );
    const handleScroll = event => {
        const listboxNode = event.currentTarget;
        if (listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 1 && !loadAutoComplete && hasMore) {
            setPage(prev => prev + 1); // Incrementa a página
        }
    };
    const handleSnackBarClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setOpenSnackBar(false);
    };
    // AutoComplete
    useEffect(() => {
        if (inputValue) {
            fetchOptions(inputValue, page);
        }
    }, [inputValue, page]);
    useEffect(() => {
        initialFetch();
    }, []);
    // Table
    useEffect(() => {
        if (!refetching) {
            setRefetching(true);
            fetchData();
        }
    }, [pagination, filter, sort]);
    return (<Box>
      <Stack direction='row' justifyContent='flex-start' alignItems='center'>
        <Typography variant='h6' sx={{ color: 'black' }}>
          Oportunidades
        </Typography>
        {client !== undefined ? (<Box>
            {expand ? (<Tooltip title={'Cancelar'}>
                <IconButton color='primary' onClick={() => setExpand(!expand)}>
                  <MdiIcon path={mdiChevronUp} sx={{ color: '#1976D2' }}/>
                </IconButton>
              </Tooltip>) : (<Box></Box>)}
            <Tooltip title={'Adicionar Oportunidade'}>
              <IconButton color='primary' onClick={() => {
                if (expand) {
                    console.log(formik.values);
                    formik.handleSubmit();
                }
                else {
                    formik.resetForm();
                    setDep('');
                    setSelected(null);
                    setExpand(!expand);
                }
            }}>
                <MdiIcon path={!expand ? mdiPlus : mdiContentSave} sx={{ color: '#1976D2' }}></MdiIcon>
              </IconButton>
            </Tooltip>
          </Box>) : (<></>)}
      </Stack>

      <Collapse in={expand}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4} lg={4}>
            <TextField fullWidth id='servico' label='Serviço' color='info' size='small' onChange={formik.handleChange} value={formik.values?.servico} error={formik.touched.servico && Boolean(formik.errors.servico)} helperText={formik.touched.servico && formik.errors.servico}/>
          </Grid>
          <Grid item xs={12} md={4} lg={4}>
            <FormControl fullWidth size='small' error={formik.touched.coreDepartamentoId && Boolean(formik.errors.coreDepartamentoId)}>
              <InputLabel id='demo-simple-select-label' color='info' error={formik.touched.coreDepartamentoId && Boolean(formik.errors.coreDepartamentoId)}>
                Área
              </InputLabel>
              <Select labelId='demo-simple-select-label' id='demo-simple-select' label='Área ' value={dep} onChange={handleSelectChange} color='info' error={formik.touched.coreDepartamentoId && Boolean(formik.errors.coreDepartamentoId)}>
                {departamentos.map(item => (<MenuItem value={item.id.toString()} key={item.id}>
                    {item.designacao}
                  </MenuItem>))}
              </Select>
              <FormHelperText>{formik.touched.coreDepartamentoId && formik.errors.coreDepartamentoId}</FormHelperText>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4} lg={4}>
            <Autocomplete disablePortal={false} // lista não fica dentro da dialog
     options={optionsAutoComplete} getOptionLabel={option => (option.name ? `${option.name}` : '')} value={selected || null} // O valor completo da opção selecionada
     onChange={(event, newValue) => {
            setSelected(newValue);
            formik.setFieldValue('colaboradorOrigem', newValue.id);
        }} 
    //filterOptions={x => x} // Evita o filtro padrão
    onInputChange={(event, newInputValue, reason) => {
            if (reason !== 'reset') {
                setInputValue(newInputValue); // Só altera o input se for uma entrada do user
                setOptionsAutoComplete([]);
                setPage(0);
                setHasMore(true);
            }
        }} ListboxProps={{
            onScroll: handleScroll // Adiciona evento de scroll
        }} renderOption={(props, option) => (<ListItem {...props} key={option.id} dense>
                  <ListItemText primary={option.name} secondary={<Stack direction='column'>
                        <Typography variant='caption'>Email: {option.email ?? 'Sem email'}</Typography>
                        <Typography variant='caption'>Nº Mecanográfico: {option.num ?? 'Não tem'}</Typography>
                      </Stack>}/>
                </ListItem>)} renderInput={params => (<TextField {...params} label='Colaborador Origem' variant='outlined' size='small' fullWidth error={formik.errors.colaboradorOrigem && Boolean(formik.errors.colaboradorOrigem)} helperText={formik.touched.colaboradorOrigem && formik.errors.colaboradorOrigem} InputProps={{
                ...params.InputProps,
                endAdornment: (<>
                        {loadAutoComplete ? <CircularProgress color='inherit' size={20}/> : null}
                        {params.InputProps.endAdornment}
                      </>)
            }}/>)}/>
          </Grid>

          <Grid item xs={12} md={12} lg={12}>
            <TextField fullWidth id='observacoes' label='Observações' color='info' size='small' multiline minRows={2} onChange={formik.handleChange} value={formik.values?.observacoes} error={formik.touched.observacoes && Boolean(formik.errors.observacoes)} helperText={formik.touched.observacoes && formik.errors.observacoes}/>
          </Grid>
        </Grid>
      </Collapse>

      <Box sx={{
            mt: 1,
            borderRadius: '15px',
            border: '1px solid #e0e0e0',
            overflow: 'hidden',
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)'
        }}>
        <Table storageKey={$state.current.name} data={data} columns={!client ? vendedorColumns : clientColumns} initial={{
            sort: sort,
            filter: filter
        }} isLoading={refetching} options={options} onPaginate={setPagination} onFilter={setFilter} onSort={setSort} rowActionSize={150} actionItems={({ row }) => [
            <Box key={`actions-${row.original.id}`} gap={1} sx={{
                    width: '100%', // Ocupa toda a largura da célula
                    height: '100%', // Ocupa toda a altura da célula
                    display: 'flex',
                    justifyContent: 'center', // Centra horizontalmente
                    alignItems: 'center' // Centra verticalmente
                }}>
              <Stack direction='row' justifyContent='center' alignItems='center'>
                <Tooltip title={'Ver detalhes'} placement='top'>
                  <IconButton color='secondary' onClick={() => {
                    const showData = [
                        { key: 'Data de Registo', value: moment(row.original.dataRegisto).format('DD-MM-YYYY / HH:mm') },
                        { key: 'Registado por', value: row.original.Funcionario.name },
                        { key: 'Serviço', value: row.original.servico },
                        { key: 'Colaborador Origem', value: row.original.ColaboradorOrigem.name },
                        { key: 'Área', value: row.original.Departamento.designacao },
                        { key: 'Observações', value: row.original.observacoes }
                    ];
                    if (row.original.dataEdit) {
                        showData.splice(1, 0, { key: 'Data de Edição', value: moment(row.original.dataEdit).format('DD-MM-YYYY / HH:mm') });
                    }
                    showModal(InfoDialog, {
                        title: 'Detalhes da Oportunidade',
                        data: showData
                    });
                }}>
                    <MdiIcon path={mdiMagnify} sx={{ color: '#1976D2' }}/>
                  </IconButton>
                </Tooltip>

                {user !== undefined ? (<></>) : (<>
                    <Tooltip title={'Editar dados'} placement='left'>
                      <IconButton color='primary' onClick={() => {
                        setExpand(true);
                        setDep(row.original.coreDepartamentoId);
                        setSelected({ id: row.original.ColaboradorOrigem.id, name: row.original.ColaboradorOrigem.name, email: row.original.ColaboradorOrigem?.email, num: row.original.ColaboradorOrigem?.numeroMecanografico });
                        formik.setValues(row.original);
                    }}>
                        <MdiIcon path={mdiPencil} sx={{ color: '#1976D2' }}/>
                      </IconButton>
                    </Tooltip>

                    <Tooltip title={'Remover Item'} placement='top'>
                      <IconButton color='error' onClick={() => {
                        const wait = showModal(ConfirmDialog, {
                            title: 'Eliminar Oportunidade',
                            message: `Tem a certeza que pretende eliminar a Oportunidade "${row.original.servico}"?`,
                            onClose: async (value) => {
                                console.log(value);
                                if (value) {
                                    let deleteOp = row.original;
                                    deleteOp.active = 0;
                                    try {
                                        await CrmService.updateOportunidade(deleteOp);
                                    }
                                    catch (error) {
                                        console.log('Erro ', error);
                                    }
                                    setRefetching(true);
                                    fetchData();
                                    setExpand(false);
                                }
                            }
                        });
                    }}>
                        <MdiIcon path={mdiTrashCan}/>
                      </IconButton>
                    </Tooltip>
                  </>)}
              </Stack>
            </Box>
        ]}></Table>
      </Box>

      <Snackbar open={openSnackBar} autoHideDuration={4000} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} onClose={handleSnackBarClose}>
        <Alert onClose={handleSnackBarClose} severity={snackBarSeverity} variant='filled' sx={{ width: '100%' }}>
          {snackBarMessage}
        </Alert>
      </Snackbar>
    </Box>);
};
export default Oportunidades;
